import type { HttpClient } from './client/http';
import type { UploadResponse } from './client/types';
import { mapError } from './client/errors';

const HOUR_MS = 3_600_000;

/**
 * Wave 5 #40: Caller-side idempotency key.
 *
 * Format: SHA-256 hex of (file bytes ‖ '|' ‖ uploader_id ‖ '|' ‖ hour-bucket).
 * Hour-bucket = Math.floor(Date.now() / 3_600_000). Same bucket + same file +
 * same user → same key. Backend ledger TTL is also ~1h, so on retry inside
 * the bucket the existing URL is returned instead of producing a duplicate
 * R2 object. After bucket rollover, identical content gets a fresh upload.
 *
 * Returns `null` when WebCrypto is unavailable or any step throws — caller
 * skips the header and the legacy non-idempotent path runs.
 */
async function readFileBytes(file: File | Blob): Promise<Uint8Array> {
  // Prefer File.arrayBuffer when available; jsdom's File polyfill is missing
  // it but ships a working Response constructor — fall back through that.
  if (typeof (file as Blob).arrayBuffer === 'function') {
    return new Uint8Array(await (file as Blob).arrayBuffer());
  }
  const buf = await new Response(file as Blob).arrayBuffer();
  return new Uint8Array(buf);
}

async function computeIdempotencyKey(
  file: File | Blob,
  uploaderId: string,
): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const fileBuf = await readFileBytes(file);
    const meta = new TextEncoder().encode(
      `|${uploaderId}|${Math.floor(Date.now() / HOUR_MS)}`,
    );
    const combined = new Uint8Array(fileBuf.length + meta.length);
    combined.set(fileBuf, 0);
    combined.set(meta, fileBuf.length);
    const digest = await crypto.subtle.digest('SHA-256', combined);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (const b of bytes) hex += b.toString(16).padStart(2, '0');
    return hex;
  } catch {
    return null;
  }
}

/**
 * @throws SdkError on any failure (NOT retried — fail-fast).
 *
 * Idempotency: when `getUserId()` returns a string, the SDK derives an
 * `Idempotency-Key` header from (file content + uploader_id + hour-bucket).
 * A second call with the same file + same user inside the same hour returns
 * the same URL without re-uploading to R2. See backend
 * `upload_ledger` table (migration 0030).
 */
export function createUpload(
  client: HttpClient,
  appId: string,
  getUserId: () => string | null,
) {
  const path = `/api/app/${appId}/upload`;
  return async function upload(file: File | Blob): Promise<UploadResponse> {
    const fd = new FormData();
    fd.append('file', file);
    const uploaderId = getUserId();
    let headers: Record<string, string> | undefined;
    if (uploaderId) {
      const key = await computeIdempotencyKey(file, uploaderId);
      if (key) headers = { 'Idempotency-Key': key };
    }
    const res = await client.requestRaw('POST', path, { body: fd, headers });
    if (!res.ok) throw await mapError(res);
    return (await res.json()) as UploadResponse;
  };
}
